import React, { useState, useEffect, useMemo, useCallback } from 'react';
import type { LayerConfig, ColorCodeConfig } from '../../../types/metadata';
import { DEFAULT_COLOR_CODE_PALETTE } from '../../../types/metadata';
import { useMapStore } from '../store/useMapStore';
import { metadataService } from '../../../services/metadataService';

interface ColorCodeLayerConfigProps {
  layer: LayerConfig;
  config: ColorCodeConfig | undefined;
}

const rgbToHex = (rgb: [number, number, number]): string => {
  return '#' + rgb.map(x => {
    const hex = x.toString(16).toUpperCase();
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
};

const hexToRgb = (hex: string): [number, number, number] => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : [255, 255, 255];
};

export const ColorCodeLayerConfig: React.FC<ColorCodeLayerConfigProps> = ({ layer, config }) => {
  const updateLayerConfig = useMapStore((state) => state.updateLayerConfig);
  const layersConfig = useMapStore((state) => state.layersConfig);

  // All available layers from metadata (for the source selector)
  const [allAvailableLayers, setAllAvailableLayers] = useState<LayerConfig[]>([]);

  useEffect(() => {
    const fetchAllLayers = async () => {
      try {
        const metadata = await metadataService.listMetadata();
        setAllAvailableLayers(metadata.files);
      } catch (error) {
        console.error('[ColorCodeLayerConfig] Failed to fetch all layers:', error);
      }
    };
    fetchAllLayers();
  }, []);

  // Get field names from this layer's schema
  const thisLayerFields = Object.keys(layer.schema || {});

  // Get field names from the selected source layer's schema
  const sourceLayerFields = useMemo(() => {
    if (!config?.sourceLayerId) return [];
    // First try loaded layers
    const loadedLayer = layersConfig[config.sourceLayerId];
    if (loadedLayer?.schema) return Object.keys(loadedLayer.schema);
    // Fallback to metadata list
    const metaLayer = allAvailableLayers.find(l => l.id === config.sourceLayerId);
    if (metaLayer?.schema) return Object.keys(metaLayer.schema);
    return [];
  }, [config?.sourceLayerId, layersConfig, allAvailableLayers]);

  // Working config: use persisted config or a local default
  const workingConfig: ColorCodeConfig = config || {
    sourceLayerId: '',
    joinField: '',
    sourceJoinField: '',
    palette: [...DEFAULT_COLOR_CODE_PALETTE],
  };

  const updateConfig = useCallback((updates: Partial<ColorCodeConfig>) => {
    const newConfig = {
      ...workingConfig,
      ...updates,
    };
    updateLayerConfig(layer.id, { colorCodeConfig: newConfig });
  }, [workingConfig, layer.id, updateLayerConfig]);

  const handleSourceLayerChange = (sourceLayerId: string) => {
    if (!sourceLayerId) {
      // Clearing the source disables color coding
      updateLayerConfig(layer.id, { colorCodeConfig: undefined });
      return;
    }
    updateConfig({
      sourceLayerId,
      sourceJoinField: '', // Reset source join field when source changes
      colorAssignments: {}, // Reset assignments
    });
  };

  const handleJoinFieldChange = (joinField: string) => {
    updateConfig({ joinField, colorAssignments: {} });
  };

  const handleSourceJoinFieldChange = (sourceJoinField: string) => {
    updateConfig({ sourceJoinField, colorAssignments: {} });
  };

  // Palette management
  const handleColorChange = (index: number, hex: string) => {
    const newPalette = [...workingConfig.palette];
    newPalette[index] = hexToRgb(hex);
    updateConfig({ palette: newPalette as [number, number, number][] });
  };

  const handleAddColor = () => {
    const newPalette = [...workingConfig.palette];
    const extraColors: [number, number, number][] = [
      [188, 189, 34],   // Olive
      [127, 127, 127],  // Gray
      [255, 187, 120],  // Light orange
      [152, 223, 138],  // Light green
      [255, 152, 150],  // Light red
      [197, 176, 213],  // Light purple
    ];
    const nextColor = extraColors[newPalette.length % extraColors.length] || [100, 100, 100];
    newPalette.push(nextColor);
    updateConfig({ palette: newPalette as [number, number, number][] });
  };

  const handleRemoveColor = (index: number) => {
    const newPalette = [...workingConfig.palette];
    if (newPalette.length <= 2) return;
    newPalette.splice(index, 1);
    updateConfig({ palette: newPalette as [number, number, number][], colorAssignments: {} });
  };

  const handleResetPalette = () => {
    updateConfig({ palette: [...DEFAULT_COLOR_CODE_PALETTE], colorAssignments: {} });
  };

  // Get source layer name for display
  const sourceLayerName = useMemo(() => {
    if (!workingConfig.sourceLayerId) return '';
    const loaded = layersConfig[workingConfig.sourceLayerId];
    if (loaded) return loaded.name;
    const meta = allAvailableLayers.find(l => l.id === workingConfig.sourceLayerId);
    return meta?.name || workingConfig.sourceLayerId;
  }, [workingConfig.sourceLayerId, layersConfig, allAvailableLayers]);

  return (
    <div>
      <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 500 }}>Color Code Data</h3>

      {/* Source Layer Selection — always visible */}
      <div className="config-group">
        <label>Select Source</label>
        <select
          value={workingConfig.sourceLayerId}
          onChange={(e) => handleSourceLayerChange(e.target.value)}
          className="field-select"
        >
          <option value="">-- None (Disabled) --</option>
          {allAvailableLayers
            .filter(l => l.id !== layer.id)
            .map(l => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))
          }
        </select>
        {!workingConfig.sourceLayerId && (
          <div style={{ fontSize: '11px', color: '#999', marginTop: '6px' }}>
            Select another layer to color-code points by matching field values.
          </div>
        )}
        {workingConfig.sourceLayerId && (
          <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
            Points from <strong>{layer.name}</strong> will be color-coded based on matching records in <strong>{sourceLayerName}</strong>
          </div>
        )}
      </div>

      {/* Join Field Configuration */}
      {workingConfig.sourceLayerId && (
        <div className="config-group">
          <div className="config-group-header">
            <label>Join Fields</label>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>{layer.name} field</div>
              <select
                value={workingConfig.joinField}
                onChange={(e) => handleJoinFieldChange(e.target.value)}
                className="field-select"
              >
                <option value="">-- Select Field --</option>
                {thisLayerFields.map(field => (
                  <option key={field} value={field}>{field}</option>
                ))}
              </select>
            </div>
            <div style={{ padding: '14px 4px 0', fontWeight: 500, color: '#666' }}>=</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>{sourceLayerName} field</div>
              <select
                value={workingConfig.sourceJoinField}
                onChange={(e) => handleSourceJoinFieldChange(e.target.value)}
                className="field-select"
              >
                <option value="">-- Select Field --</option>
                {sourceLayerFields.map(field => (
                  <option key={field} value={field}>{field}</option>
                ))}
              </select>
            </div>
          </div>
          {workingConfig.joinField && workingConfig.sourceJoinField && (
            <div style={{ fontSize: '11px', color: '#666', marginTop: '8px' }}>
              When <strong>{sourceLayerName}</strong> points are visible, <strong>{layer.name}</strong> points will be colored by matching <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: '3px' }}>{workingConfig.joinField}</code> = <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: '3px' }}>{workingConfig.sourceJoinField}</code>
            </div>
          )}
        </div>
      )}

      {/* Color Palette */}
      {workingConfig.sourceLayerId && workingConfig.joinField && workingConfig.sourceJoinField && (
        <>
          <div className="config-group">
            <div className="config-group-header">
              <label>Color Palette</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={handleAddColor} className="add-btn">+ Add Color</button>
                <button onClick={handleResetPalette} className="add-btn" style={{ color: '#888' }}>Reset</button>
              </div>
            </div>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', 
              gap: '8px', 
              marginTop: '12px' 
            }}>
              {workingConfig.palette.map((color, index) => (
                <div 
                  key={index} 
                  style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    gap: '4px',
                    padding: '8px',
                    borderRadius: '6px',
                    border: '1px solid #eee',
                    background: '#fafafa',
                  }}
                >
                  <div style={{ 
                    width: '32px', 
                    height: '32px', 
                    borderRadius: '50%', 
                    backgroundColor: rgbToHex(color as [number, number, number]),
                    border: '2px solid #ddd',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                  }}>
                    <input
                      type="color"
                      value={rgbToHex(color as [number, number, number])}
                      onChange={(e) => handleColorChange(index, e.target.value)}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: 'pointer',
                      }}
                    />
                  </div>
                  <div style={{ fontSize: '10px', color: '#999' }}>#{index + 1}</div>
                  {workingConfig.palette.length > 2 && (
                    <button
                      onClick={() => handleRemoveColor(index)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#ccc',
                        cursor: 'pointer',
                        fontSize: '14px',
                        lineHeight: 1,
                        padding: '0 2px',
                      }}
                      title="Remove color"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div style={{ fontSize: '11px', color: '#999', marginTop: '8px' }}>
              Each unique value visible on the map will be assigned the next available color. Colors are sticky — they maintain their previous assignment.
            </div>
          </div>

          {/* Non-matched Opacity */}
          <div className="config-group">
            <div className="config-group-header">
              <label>Non-matched Opacity</label>
              <span style={{ fontSize: '12px', color: '#666' }}>
                {Math.round((workingConfig.unmatchedOpacity ?? 0.2) * 100)}%
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              step="5"
              value={Math.round((workingConfig.unmatchedOpacity ?? 0.2) * 100)}
              onChange={(e) => updateConfig({ unmatchedOpacity: parseInt(e.target.value) / 100 })}
              style={{ width: '100%', marginTop: '8px' }}
            />
            <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
              Points that don't match a visible color-coded value will be shown at this opacity.
            </div>
          </div>
        </>
      )}
    </div>
  );
};
